import {portalApiPost,portalApiGet,PortalApiError} from '@/lib/portal-api';
import {moneyOperationSession} from './money-operation-session';
export interface CloseIntent {
 clinicId:number;businessDate:string;countedCashMinorUnits:number;expectedDerivedCloseMinorUnits:number;
 differenceReasonCategory:string|null;differenceReasonText:string|null;verifiedByDoctorId:number|null;
}
export interface SavedClose {
 intent:CloseIntent;checkId:string;requestId:string;phase:'requesting'|'checking'|'closing';
}
export interface CloseReview {request_id:string;check_id:string;clinic_id:number;decision:'confirm'|'send_back';note:string|null;reviewed_at:string}
export interface CloseCheck {
 calculation?:{dayStartMinorUnits:number;cashInMinorUnits:number;spentMinorUnits:number;sentMinorUnits:number;receivedMinorUnits:number;cashNowMinorUnits:number}|null;
 clinicName?:string|null;checkerName?:string|null;counterName?:string|null;requestedAt?:string|null;sentBack?:boolean;review?:CloseReview|null;
 checkId:string;confirmed:boolean;confirmedAt:string|null;sourceChanged?:boolean;closed?:boolean;verifierRequired?:boolean;
 context:{counterRole?:string;dayStartCashMinorUnits?:number;businessDate:string;countedCloseMinorUnits:number;derivedCloseMinorUnits:number;differenceMinorUnits:number;differenceReasonCategory:string|null;differenceReasonText:string|null;verifiedByDoctorId:number|null};
}
function read(key:string):SavedClose|null {
 const raw=sessionStorage.getItem(key);if(raw===null)return null;
 const x=JSON.parse(raw) as SavedClose;
 if(!x?.intent||!Number.isSafeInteger(x.intent.clinicId)||!Number.isSafeInteger(x.intent.countedCashMinorUnits)||
  !Number.isSafeInteger(x.intent.expectedDerivedCloseMinorUnits)||!/^\d{4}-\d{2}-\d{2}$/.test(x.intent.businessDate)||
  !['requesting','checking','closing'].includes(x.phase)||!/^[-a-f0-9]{36}$/.test(x.checkId)||!/^[-a-f0-9]{36}$/.test(x.requestId))throw new Error('The saved close could not be read. Keep this session and ask for recovery.');
 return x;
}
function save(key:string,x:SavedClose) {sessionStorage.setItem(key,JSON.stringify(x));if(JSON.stringify(read(key))!==JSON.stringify(x))throw new Error('Recovery could not be saved. Nothing was sent.');}
export async function pendingClose(clinicId:number,date:string) {
 const s=await moneyOperationSession('close');const x=read(s.key);await s.assertCurrent();
 if(x&&(x.intent.clinicId!==clinicId||x.intent.businessDate!==date))throw new Error(`Resolve the saved close for ${x.intent.businessDate} in its original branch first.`);
 return x;
}
export async function requestCloseCheck(intent:CloseIntent):Promise<{saved:SavedClose;check:CloseCheck}> {
 const s=await moneyOperationSession('close');let x=read(s.key);
 if(x&&JSON.stringify(x.intent)!==JSON.stringify(intent))throw new Error('Resolve the saved count before changing it.');
 if(!x){x={intent,checkId:crypto.randomUUID(),requestId:crypto.randomUUID(),phase:'requesting'};save(s.key,x);}
 await s.assertCurrent();
 try {
  const out=await portalApiPost<{check:CloseCheck}>('/accounts/day/check',{...x.intent,requestId:x.checkId},s.bearer);
  await s.assertCurrent();
  if(out?.check?.checkId!==x.checkId||typeof out.check.confirmed!=='boolean'||typeof out.check.verifierRequired!=='boolean'||
    out.check.context?.countedCloseMinorUnits!==x.intent.countedCashMinorUnits||out.check.context?.derivedCloseMinorUnits!==x.intent.expectedDerivedCloseMinorUnits)throw new Error('The saved check could not be confirmed. Retry this count.');
  x={...x,phase:'checking'};save(s.key,x);return {saved:x,check:out.check};
 }catch(e){if(e instanceof PortalApiError&&['source_changed','VALIDATION_FAILED'].includes(e.code??''))sessionStorage.removeItem(s.key);throw e;}
}
export async function readCloseCheck(clinicId:number,checkId:string):Promise<CloseCheck> {
 const s=await moneyOperationSession('close');
 const out=await portalApiGet<{check:CloseCheck}>(`/accounts/day/check?clinicId=${clinicId}&checkId=${encodeURIComponent(checkId)}`,s.bearer);
 await s.assertCurrent();
 if(out?.check?.checkId!==checkId||typeof out.check.confirmed!=='boolean'||typeof out.check.sourceChanged!=='boolean'||!out.check.context)throw new Error('The saved check could not be read.');
 return out.check;
}
export async function confirmCloseCheck(clinicId:number,checkId:string) {
 const s=await moneyOperationSession('close');
 const out=await portalApiPost<{check:CloseCheck}>('/accounts/day/check/confirm',{clinicId,checkId},s.bearer);
 await s.assertCurrent();if(out?.check?.checkId!==checkId||out.check.confirmed!==true||!out.check.confirmedAt)throw new Error('Confirmation could not be verified. Reopen this check.');
 return out.check;
}
export async function finishClose(saved:SavedClose) {
 const s=await moneyOperationSession('close');const x=read(s.key);
 if(!x||JSON.stringify(x.intent)!==JSON.stringify(saved.intent)||x.requestId!==saved.requestId)throw new Error('Reopen the original saved close.');
 save(s.key,{...x,phase:'closing'});await s.assertCurrent();
 try {
 const out=await portalApiPost<{closedDay:{countedCloseMinorUnits:number};businessDate:string;created:boolean;closeContract:string}>('/accounts/day/close',{...x.intent,requestId:x.requestId,checkId:x.checkId},s.bearer);
 await s.assertCurrent();
 if(out?.businessDate!==x.intent.businessDate||typeof out.created!=='boolean'||out.closeContract!=='atomic'||out.closedDay?.countedCloseMinorUnits!==x.intent.countedCashMinorUnits)throw new Error('The close could not be confirmed. Retry this saved close.');
 sessionStorage.removeItem(s.key);return out;
 }catch(e){
  if(e instanceof PortalApiError&&['source_changed','close_check_changed','VALIDATION_FAILED','FORBIDDEN'].includes(e.code??''))save(s.key,{...x,phase:'checking'});
  throw e;
 }
}
/** Checking cannot save a close. A confirmed read proves no close committed. */
export async function changeSavedCount(clinicId:number,date:string) {
 const s=await moneyOperationSession('close');const x=read(s.key);
 if(!x)return;
 if(x.intent.clinicId!==clinicId||x.intent.businessDate!==date||x.phase!=='checking')throw new Error('Resolve the saved request before changing this count.');
 const check=await readCloseCheck(clinicId,x.checkId);
 await s.assertCurrent();if(check.closed)throw new Error('This day is already closed. Open its saved close.');
 sessionStorage.removeItem(s.key);
}
